<template>
  <div class="mt-4">
    <Card class="px-1 h-fit w-full my-auto" pt:content:class="p-0!">
      <template #header>
        <div v-if="errors.length" class="flex flex-col gap-2">
          <Message severity="error" :closeable="false">
            <div class="flex flex-col gap-1">
              <p v-for="error in errors">{{ error }}</p>
            </div>
          </Message>
        </div>
      </template>
      <template #content>
        <form @submit="addLedger">
          <div class="flex flex-col gap-2">
            <StackedLabelDropdown
              name="userGroup"
              label="User Group"
              :options="userGroups"
              options-label="name"
              placeholder="Select a user group"
              classes="required"
              :disabled="userGroups.length <= 1"
            />
            <StackedLabelInput name="description" label="Description" classes="required" />
            <StackedLabelInput name="bankName" label="Bank Name" classes="required" />
            <StackedLabelInput name="bankABANumber" label="Bank ABA Number" classes="required" />
            <StackedLabelInput name="accountNumber" label="Account Number" classes="required" />
            <StackedLabelInputNumber name="nextCheckNumber" label="Next Check #" />
            <LabeledCheckbox name="active" label="Active" />
          </div>
        </form>
      </template>
      <template #footer>
        <div class="flex justify-end gap-2 mt-4">
          <Button label="Add" @click="addLedger" />
          <Button label="Cancel" severity="surface" @click="$emit('cancelled')" />
        </div>
      </template>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import { z } from "zod";
import { CodeSchema, type Code } from "@/types/Codes";
import { getUserGroups } from "@/services/Utility";

const errors = ref<string[]>([]);
const userGroups = ref<Code[]>([]);

onBeforeMount(async (): Promise<void> => {
  await getUserGroups(true, true).then((codes: Code[]) => (userGroups.value = codes));
  if (userGroups.value.length == 1) setFieldValue("userGroup", userGroups.value[0]);
});

const emit = defineEmits<{
  added: [];
  cancelled: [];
}>();

const { handleSubmit, setFieldValue } = useForm({
  validationSchema: toTypedSchema(
    z.object({
      description: z.string(),
      bankName: z.string(),
      bankABANumber: z.string(),
      accountNumber: z.string(),
      nextCheckNumber: z.number().nullable(),
      active: z.boolean(),
      userGroup: CodeSchema,
    })
  ),
  initialValues: {
    active: true,
  },
});

const addLedger = handleSubmit((values: any): void => {
  axios.post("/ledger/add", values).then((response) => {
    // Clear existing errors
    while (errors.value.length) {
      errors.value.pop();
    }

    if (response.data.errors) {
      errors.value.push("Please fix the following errors:");

      for (let error of response.data.errors) {
        errors.value.push(error.message);
      }
    } else {
      success("Success", "Ledger added");
    }

    emit("added");
  });
});
</script>
